import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { CommissionDetailOut, CommissionOut, CommissionRow, OutletRevenueOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Kpi } from '../components/Kpi'
import { Panel } from '../components/States'
import { MonthPicker } from '../components/MonthPicker'
import { StatusChip } from '../components/StatusChip'
import { useParam } from '../lib/filters'
import { count, formatDate, money, num } from '../lib/format'
import { downloadCsv } from '../lib/csv'

/** Previous month — the one you would normally be paying out. */
function lastMonth(): string {
  const now = new Date()
  const date = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function monthLabelLong(month: string): string {
  const [year, mon] = month.split('-')
  if (!year || !mon) return month
  return new Date(Number(year), Number(mon) - 1, 1).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  })
}

type SortKey = 'name' | 'sales_ex_tax' | 'invoice_count' | 'commission'

function SortHeader({
  label,
  sortKey,
  sort,
  direction,
  onSort,
  numeric = false,
}: {
  label: string
  sortKey: SortKey
  sort: SortKey
  direction: 'asc' | 'desc'
  onSort: (key: SortKey) => void
  numeric?: boolean
}) {
  const active = sort === sortKey
  return (
    <th
      className={`sortable${numeric ? ' num' : ''}`}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {active ? <span className="arrow">{direction === 'desc' ? '▼' : '▲'}</span> : null}
    </th>
  )
}

/**
 * The invoices behind one person's figure. Fetched only when the row is
 * opened — a branch can have a dozen salespeople and nobody wants twelve
 * queries for a page they are reading top-line numbers off.
 */
function PersonDetail({
  row,
  month,
  outlet,
  rate,
  columns,
}: {
  row: CommissionRow
  month: string
  outlet: string
  rate: string
  columns: number
}) {
  const query = useQuery({
    queryKey: ['commission-detail', month, outlet, rate, row.sales_person_id, row.name],
    queryFn: () =>
      api.get<CommissionDetailOut>('/analytics/commission/detail', {
        month,
        outlet_id: outlet || null,
        sales_person_id: row.sales_person_id,
        // Sales HIKE never linked to a staff record are identified by name.
        name: row.sales_person_id ? null : row.name,
        rate: Number(rate) || 1,
      }),
  })

  const detail = query.data

  return (
    <tr>
      <td colSpan={columns} style={{ background: 'var(--page)', padding: '12px 16px' }}>
        <Panel
          isLoading={query.isPending}
          error={query.error}
          isEmpty={detail?.invoices.length === 0}
          emptyText="No invoices behind this figure."
        >
          {detail ? (
            <>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                <h3>{detail.invoices.length} invoices making up {money(detail.total_sales_ex_tax)}</h3>
                {detail.excluded_layby_count > 0 ? (
                  <span className="chip amber">
                    {count(detail.excluded_layby_count)} layby excluded ·{' '}
                    {money(detail.excluded_layby_sales_ex_tax)}
                  </span>
                ) : null}
              </div>

              <div className="card">
                <div className="card-body flush">
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th className="num">Completed</th>
                          <th>Customer</th>
                          <th>Status</th>
                          <th className="num">Sales (ex. tax)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.invoices.map((invoice) => (
                          <tr key={invoice.invoice_id}>
                            <td>
                              <Link to={`/invoices/${invoice.invoice_id}`}>
                                {invoice.hike_invoice_number ?? invoice.reference}
                              </Link>
                            </td>
                            <td className="num cell-sub">{formatDate(invoice.completed)}</td>
                            <td>{invoice.customer_name}</td>
                            <td>
                              <StatusChip status={invoice.status} />
                            </td>
                            <td className="num">{money(invoice.sales_ex_tax)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="strong">Total</td>
                          <td colSpan={3} />
                          <td className="num strong">{money(detail.total_sales_ex_tax)}</td>
                        </tr>
                        <tr>
                          <td className="strong">Commission at {detail.rate_pct}%</td>
                          <td colSpan={3} />
                          <td className="num strong">{money(detail.commission)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </td>
    </tr>
  )
}

export function CommissionPage() {
  const [params, setParams] = useSearchParams()
  const month = params.get('month') || lastMonth()
  const outlet = params.get('outlet') ?? ''
  const [rate, setRate] = useParam<string>('rate', '1')
  const [open, setOpen] = useState<string | null>(null)
  const [sort, setSort] = useState<SortKey>('sales_ex_tax')
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc')

  function rowKey(row: CommissionRow): string {
    return row.sales_person_id ?? row.name
  }

  function sortBy(key: SortKey) {
    if (key === sort) {
      setDirection(direction === 'desc' ? 'asc' : 'desc')
    } else {
      setSort(key)
      // Names read best A-Z; money and counts read best biggest-first.
      setDirection(key === 'name' ? 'asc' : 'desc')
    }
  }

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const outlets = useQuery({
    queryKey: ['outlets'],
    staleTime: 60 * 60 * 1000,
    queryFn: () =>
      api.get<OutletRevenueOut>('/analytics/revenue/by-outlet', {
        from_date: '2020-01-01',
        to_date: new Date().toISOString().slice(0, 10),
        compare: 'none',
        scope: 'all',
      }),
  })

  const query = useQuery({
    queryKey: ['commission', month, outlet, rate],
    queryFn: () =>
      api.get<CommissionOut>('/analytics/commission', {
        month,
        outlet_id: outlet || null,
        rate: Number(rate) || 1,
      }),
  })

  const data = query.data

  const rows = useMemo(() => {
    const source = [...(data?.rows ?? [])]
    const factor = direction === 'asc' ? 1 : -1
    return source.sort((a, b) => {
      if (sort === 'name') return factor * a.name.localeCompare(b.name)
      if (sort === 'invoice_count') return factor * (a.invoice_count - b.invoice_count)
      return factor * (num(a[sort]) - num(b[sort]))
    })
  }, [data?.rows, sort, direction])

  async function exportPdf() {
    if (!data) return
    // Loaded on demand: jsPDF is far too large to sit in the initial bundle.
    const { exportCommissionPdf } = await import('../lib/commissionPdf')
    exportCommissionPdf(data, rows)
  }

  function exportCsv() {
    if (!data) return
    const where = data.outlet_name ?? 'All branches'
    const lines = [
      [`Commission — ${where} — ${monthLabelLong(data.month)}`],
      [`Rate`, `${data.rate_pct}%`],
      [],
      ['Salesperson', 'Sales (ex. tax)', 'Invoices', 'Commission'],
      ...rows.map((row) => [
        row.name,
        num(row.sales_ex_tax).toFixed(2),
        String(row.invoice_count),
        num(row.commission).toFixed(2),
      ]),
      [],
      ['Total', num(data.total_sales_ex_tax).toFixed(2), '', num(data.total_commission).toFixed(2)],
    ]
    downloadCsv(`Commission ${where} ${data.month}.csv`, lines)
  }

  return (
    <>
      <PageHeader
        title="Commission"
        subtitle={`${monthLabelLong(month)}${data?.outlet_name ? ` · ${data.outlet_name}` : ''}`}
      >
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={exportCsv} disabled={!data?.rows.length}>
            Export CSV
          </button>
          <button className="btn primary" onClick={exportPdf} disabled={!data?.rows.length}>
            Export PDF
          </button>
        </div>
      </PageHeader>

      <div className="content stack">
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field">
            <label>Month</label>
            <MonthPicker value={month} onChange={(next) => setParam('month', next)} />
          </div>

          <div className="field">
            <label htmlFor="branch">Branch</label>
            <select
              id="branch"
              value={outlet}
              onChange={(event) => setParam('outlet', event.target.value)}
            >
              <option value="">All branches</option>
              {(outlets.data?.items ?? [])
                .filter((row) => row.outlet_id !== null && row.outlet_name)
                .map((row) => (
                  <option key={row.outlet_id} value={row.outlet_id!}>
                    {row.outlet_name}
                  </option>
                ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="rate">Rate %</label>
            <input
              id="rate"
              type="number"
              min={0}
              max={100}
              step={0.1}
              style={{ width: 82 }}
              value={rate}
              onChange={(event) => setRate(event.target.value)}
            />
          </div>
        </div>

        <Panel isLoading={query.isPending} error={query.error}>
          {data ? (
            <>
              <div className="grid cols-3">
                <Kpi label="Total sales (ex. tax)" value={money(data.total_sales_ex_tax)} />
                <Kpi
                  label={`Commission at ${data.rate_pct}%`}
                  value={money(data.total_commission)}
                />
                <Kpi label="Salespeople" value={count(data.rows.length)} />
              </div>

              {data.excluded.layby_count > 0 ? (
                <div className="alert warn">
                  <strong>
                    {count(data.excluded.layby_count)} layby sales worth{' '}
                    {money(data.excluded.layby_sales_ex_tax)} are not counted above.
                  </strong>{' '}
                  HIKE's sales report leaves laybys out, so no commission has ever been paid on
                  one. That is reproduced here so payouts do not change — but it may well be a bug
                  worth deciding on, which is why the figure is shown rather than dropped.
                </div>
              ) : null}

              <div className="card">
                <div className="card-head">
                  <h2>{data.outlet_name ?? 'All branches'}</h2>
                  <span className="hint">
                    {monthLabelLong(data.month)} · click a name to see the invoices behind it
                  </span>
                </div>
                <div className="card-body flush">
                  <Panel
                    isLoading={false}
                    error={null}
                    isEmpty={data.rows.length === 0}
                    emptyText="No sales completed in this month."
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <SortHeader
                              label="Salesperson"
                              sortKey="name"
                              sort={sort}
                              direction={direction}
                              onSort={sortBy}
                            />
                            <SortHeader
                              label="Sales (ex. tax)"
                              sortKey="sales_ex_tax"
                              sort={sort}
                              direction={direction}
                              onSort={sortBy}
                              numeric
                            />
                            <SortHeader
                              label="Invoices"
                              sortKey="invoice_count"
                              sort={sort}
                              direction={direction}
                              onSort={sortBy}
                              numeric
                            />
                            <SortHeader
                              label="Commission"
                              sortKey="commission"
                              sort={sort}
                              direction={direction}
                              onSort={sortBy}
                              numeric
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => {
                            const key = rowKey(row)
                            const isOpen = open === key
                            return [
                              <tr
                                key={key}
                                className="clickable"
                                onClick={() => setOpen(isOpen ? null : key)}
                              >
                                <td className="strong">
                                  <span
                                    className="glyph"
                                    style={{ marginRight: 6, color: 'var(--text-faint)' }}
                                  >
                                    {isOpen ? '▾' : '▸'}
                                  </span>
                                  {row.name}
                                </td>
                                <td className="num">{money(row.sales_ex_tax)}</td>
                                <td className="num">{count(row.invoice_count)}</td>
                                <td className="num strong">{money(row.commission)}</td>
                              </tr>,
                              isOpen ? (
                                <PersonDetail
                                  key={`${key}-detail`}
                                  row={row}
                                  month={month}
                                  outlet={outlet}
                                  rate={rate}
                                  columns={4}
                                />
                              ) : null,
                            ]
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td className="strong">Total</td>
                            <td className="num strong">{money(data.total_sales_ex_tax)}</td>
                            <td />
                            <td className="num strong">{money(data.total_commission)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="alert info">
                Sales are counted in the month they were <strong>completed</strong> in HIKE, for the
                branch the sale was rung up at. The figure is the order total excluding tax, after
                discount, with refunds netted off. People on a target-based scheme show the same 1%
                column — ignore it for them and use their sales total.
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
